import * as React from "react";
import {Grid2 as Grid, InputAdornment, Radio, Typography} from "@mui/material";
import {OrderDataDTO, QuoteStatus} from "@/api/data-contracts";
import {ChangeHandler, ModelAction} from "@/lib/modelstate";
import {ErrorsShape} from "@/lib/utils";
import {CardEx} from "@/lib/components/CardEx";
import CurrencyInputField from "@/lib/components/inputfields/currency-input-field";
import {aidOf} from "@/lib/oid";
import NumberInputField from "@/lib/components/inputfields/number-input-field";
import {IconButtonEx} from "@/lib/components/buttons/IconButtonEx";
import RemoveIcon from "@mui/icons-material/Remove";
import InfoTooltipIcon from "@/lib/components/InfoTooltipIcon";
import AddIcon from "@mui/icons-material/Add";
import Box from "@mui/material/Box";
import {ButtonEx} from "@/lib/components/buttons/ButtonEx";
import CheckboxInputField from "@/lib/components/inputfields/checkbox-input-field";
import RadioButtonInputField from "@/lib/components/inputfields/radio-button-field";
import {Constants} from "@/api/constants";
import {useTranslation} from "react-i18next";
import {RequestI18N} from "@/translations";
import {OrderDataType} from "@/model/orderData";
import {useCardChangeColors} from "@/common/request-cards/common";

export type PricingCardProps = {
    orderData?: OrderDataDTO;
    onChange: ChangeHandler;
    errors: ErrorsShape<{ orderData?: OrderDataDTO }>;
    dispatch: React.Dispatch<ModelAction>;
    readonly?: boolean;
    mode: OrderDataType;
    quoteIdx?: number;
    quoteStatus?: QuoteStatus;
    orgData?: OrderDataDTO | null;
};

export const PricingCard = ({
    orderData,
    orgData,
    errors,
    onChange,
    dispatch,
    readonly,
    mode,
    quoteIdx,
                                quoteStatus
}: PricingCardProps) => {
    const { t } = useTranslation();
    const { getGridChangeSx } = useCardChangeColors();
    const handleAddQuantityRow = () => {
        if (
            orderData?.quantityOptions != null &&
            orderData.quantityOptions.length < Constants.QUANTITY_OPTIONS_LENGTH_MAX
        ) {
            const index = orderData.quantityOptions.length;
            dispatch({ type: "addQuantityRow" });
            setTimeout(() => {
                const el = document.getElementsByName(`orderData.quantityOptions[${index}].quantity`)[0];
                el?.focus();
            }, 250);
        }
    };

    const handleRemoveQuantityRow = (index: number) => {
        dispatch({ type: "remQuantityRow", data: index });
    };

    React.useEffect(() => {
        if (mode === "accept" && quoteStatus === QuoteStatus.SUBMITTED && orderData?.quantityOptions?.length === 1) {
            onChange({
                name: `quotes[${quoteIdx}].orderData.quantityOptionSelected`,
                value: 0
            });
        }
    }, [mode, quoteStatus, orderData?.quantityOptions?.length, quoteIdx, onChange]);

    const readonlyEx = readonly || mode !== "request";

    const renderQuantityAdornment = (index: number) => {
        if (mode === "accept" || (mode === "quote" && readonly)) {
            return (
                    <Radio
                            name="orderData.quantityOptionSelected"
                            size="small"
                            checked={orderData?.quantityOptionSelected === index}
                            value="quantityOptionSelected"
                            disabled={mode !== "accept" || quoteStatus !== QuoteStatus.SUBMITTED}
                            onClick={() => {
                                onChange({
                                    name: `quotes[${quoteIdx}].orderData.quantityOptionSelected`,
                                    value: orderData?.quantityOptionSelected === index ? null : index
                                });
                            }}
                    />
            );
        }
        if (mode === "request" && !readonly && index > 0) {
            return (
                    <IconButtonEx
                            size="small"
                            Icon={RemoveIcon}
                            onClick={() => handleRemoveQuantityRow(index)}
                    />
            );
        }
        return <Box width="30px"/>;
    };

    const renderExtraCosts = () => {
        if (!orderData?.separateExtraCosts) {
            return (
                    <Grid size={8} marginTop={-1}>
                        <Typography variant="body1" color="textSecondary">
                            Werkzeug- und Klischee-Kosten sind in den Nettopreisen-frei-Haus-pro-1000 enthalten.
                        </Typography>
                    </Grid>
            );
        }
        if (mode === "request") {
            return null;
        }
        return (
                <>
                    <Grid
                            size={{xs: 4}}
                            sx={getGridChangeSx(orgData, orgData?.toolingCosts !== orderData?.toolingCosts)}
                    >
                        <CurrencyInputField
                                name="orderData.toolingCosts"
                                label={t(RequestI18N.details.field.toolingCosts)}
                                onChange={onChange}
                                value={orderData.toolingCosts}
                                error={errors?.orderData?.toolingCosts}
                                min={Constants.TOOLING_COSTS_PRICE_MIN}
                                max={Constants.TOOLING_COSTS_PRICE_MAX}
                                disabled={readonly}
                                helperText={
                                    orgData && orgData?.toolingCosts !== orderData?.toolingCosts
                                            ? orgData?.toolingCosts
                                            : undefined
                                }
                        />
                    </Grid>
                    <Grid
                            size={{xs: 4}}
                            sx={getGridChangeSx(orgData, orgData?.clicheCosts !== orderData?.clicheCosts)}
                    >
                        <CurrencyInputField
                                name="orderData.clicheCosts"
                                label={t(RequestI18N.details.field.clicheCosts)}
                                onChange={onChange}
                                value={orderData.clicheCosts}
                                error={errors?.orderData?.clicheCosts}
                                min={Constants.CLICHE_COSTS_PRICE_MIN}
                                max={Constants.CLICHE_COSTS_PRICE_MAX}
                                disabled={readonly}
                                helperText={
                                    orgData && orgData?.clicheCosts !== orderData?.clicheCosts
                                            ? orgData?.clicheCosts
                                            : undefined
                                }
                        />
                    </Grid>
                </>
        );
    };

    return (
        <CardEx title={t(RequestI18N.details.page.pricingCardTitle)}>
            <Grid container spacing={2} direction="column">
                <Grid container spacing={2} direction="column">
                    {orderData?.quantityOptions?.map((field, index) => (
                        <Grid key={aidOf(field)} container size={{ xs: 12 }} spacing={2} alignItems="start">
                            <Grid
                                size={{ sm: 4 }}
                                sx={getGridChangeSx(
                                    orgData,
                                    orgData?.quantityOptions?.[index]?.quantity !== field.quantity
                                )}
                            >
                                <NumberInputField
                                    name={`orderData.quantityOptions[${index}].quantity`}
                                    label={t(RequestI18N.details.field.quantity)}
                                    onChange={onChange}
                                    value={field.quantity}
                                    error={errors?.orderData?.quantityOptions?.[index]?.quantity}
                                    suffix={` ${t(RequestI18N.details.field.pcsShort)}`}
                                    min={Constants.QUANTITY_MIN}
                                    max={Constants.QUANTITY_MAX}
                                    endAdornment={
                                        <InputAdornment position="end">
                                            {renderQuantityAdornment(index)}
                                        </InputAdornment>
                                    }
                                    disabled={readonly}
                                    helperText={
                                        orgData?.quantityOptions?.[index]?.quantity === field.quantity
                                            ? undefined
                                            : orgData?.quantityOptions?.[index]?.quantity
                                    }
                                />
                            </Grid>
                            <Grid
                                size={{ sm: 4 }}
                                sx={getGridChangeSx(
                                    orgData,
                                    orgData?.quantityOptions?.[index]?.netPricePer1000 !== field.netPricePer1000
                                )}
                            >
                                <CurrencyInputField
                                    name={`orderData.quantityOptions[${index}].netPricePer1000`}
                                    label={t(RequestI18N.details.field.netPrice)}
                                    onChange={onChange}
                                    value={field.netPricePer1000}
                                    error={errors?.orderData?.quantityOptions?.[index]?.netPricePer1000}
                                    min={Constants.NET_PRICE_PER_1000_MIN}
                                    max={Constants.NET_PRICE_PER_1000_MAX}
                                    endAdornment={
                                        <InputAdornment position="end">
                                            <InfoTooltipIcon tooltipText={t(RequestI18N.details.tooltip.netPrice)}/>
                                        </InputAdornment>
                                    }
                                    disabled={readonly}
                                    helperText={
                                        orgData?.quantityOptions?.[index]?.netPricePer1000 === field.netPricePer1000
                                            ? undefined
                                            : orgData?.quantityOptions?.[index]?.netPricePer1000
                                    }
                                />
                            </Grid>
                            <Grid size={{ sm: 4 }}>
                                <CurrencyInputField
                                    name={`orderData.quantityOptions[${index}].netPriceTotal`}
                                    label={t(RequestI18N.details.field.netPriceFreeOfHouse)}
                                    disabled
                                    value={field.netPriceTotal}
                                />
                            </Grid>
                        </Grid>
                    ))}

                    {!readonlyEx &&
                    (orderData?.quantityOptions?.length == null ||
                        orderData?.quantityOptions?.length < Constants.QUANTITY_OPTIONS_LENGTH_MAX) ? (
                        <Grid size={{ xs: 12 }} container direction="row" spacing={3}>
                            <Grid size={{ xs: 6 }}>
                                <ButtonEx
                                    data-cy={"add-price-request-button"}
                                    label={t(RequestI18N.form.action.addPrice)}
                                    variant="text"
                                    startIcon={<AddIcon />}
                                    onClick={handleAddQuantityRow}
                                    size="small"
                                />
                            </Grid>
                        </Grid>
                    ) : null}
                </Grid>

                {mode === "request" ? (
                    <Grid size={{ xs: 12 }}>
                        <CheckboxInputField
                            name="orderData.separateExtraCosts"
                            label={t(RequestI18N.details.field.separateExtraCosts)}
                            onChange={onChange}
                            value={orderData?.separateExtraCosts}
                            error={errors?.orderData?.separateExtraCosts}
                            disabled={readonlyEx}
                        />
                    </Grid>
                ) : null}

                <Grid container direction="row">
                    {renderExtraCosts()}
                </Grid>

                {/* Serienauftrag + Gesamtvolumen */}
                <Grid container spacing={2} direction="row" sx={{ mt: 1 }}>
                    <Grid size={{ xs: 12, sm: 4 }}>
                        <RadioButtonInputField
                            name="orderData.seriesOrder"
                            label={t(RequestI18N.details.field.seriesOrder)}
                            onChange={onChange}
                            value={orderData?.seriesOrder ?? false}
                            disabled={readonlyEx}
                            options={[
                                {label: t(RequestI18N.details.field.seriesSingleOrder), value: false},
                                {label: t(RequestI18N.details.field.seriesSeriesOrder), value: true}
                            ]}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                        <NumberInputField
                            name="orderData.seriesTotalQuantity"
                            label={t(RequestI18N.details.field.seriesTotalQuantity)}
                            onChange={onChange}
                            value={orderData?.seriesTotalQuantity}
                            error={errors?.orderData?.seriesTotalQuantity}
                            suffix={` ${t(RequestI18N.details.field.pcsShort)}`}
                            min={Constants.SERIES_TOTAL_QUANTITY_MIN}
                            max={Constants.SERIES_TOTAL_QUANTITY_MAX}
                            disabled={readonlyEx || orderData?.seriesOrder !== true}
                        />
                    </Grid>
                </Grid>

            </Grid>
        </CardEx>
    );
};
